import { MdAddCircleOutline } from '@react-icons/all-files/md/MdAddCircleOutline'
import cuid from 'cuid'
import React, { Component } from 'react'
import { connect } from 'react-redux'
import { Link } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import api from '@disco/api/server'
import { sendGenericError } from '@disco/state-management/actions/error-actions'
import { loadTrackRightsData } from '@disco/state-management/reducers/rightsManagement'
import {
  getEditedTrackFullWriterSplits,
  getEditedTrackWriterIds,
} from '@disco/state-management/selectors/contacts'
import { getConstanceSettings } from '@disco/state-management/selectors/user'
import { parseServerError } from '@disco/utils/captureError'

import type { EditableTrack, TrackId, WriterSplit } from 'types/track'

import HelpTooltip from 'components/common/tooltip'
import CopyMetadataButton from 'components/common/tracks/copy-metadata-button'
import { Box } from 'components/ds'
import DialogFooter from 'components/modals/dialog-footer'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import ErrorText from 'components/ui/error-text'
import FormNote from 'components/ui/form-note'
import Heading from 'components/ui/heading'
import InlineLink from 'components/ui/inline-link'
import TextField from 'components/ui/text-field'

import EditWriter from './edit-writer'
import TrackWriter from './track-writer'

const mapStateToProps = state => {
  return {
    attach_writers_to_metadata: state.user.business.attach_writers_to_metadata,
    splits: getEditedTrackFullWriterSplits(state),
    splitWriterIds: getEditedTrackWriterIds(state),
    constanceSettings: getConstanceSettings(state),
  }
}

type Props = {
  disableEditing: boolean
  editedTrack: EditableTrack & {
    trackIds?: TrackId[]
  }
  attach_writers_to_metadata: boolean
  splits: Record<string, any>[]
  splitWriterIds: number[]
  onAdd: (...args: Array<any>) => any
  onUpdate: (...args: Array<any>) => any
  onDelete: (...args: Array<any>) => any
  saving: boolean
  hasUnsavedChanges: boolean
  updateEditedTrack: (...args: Array<any>) => any
  sendMixpanelEvent: (...args: Array<any>) => any
  bulkEditingMode: boolean
  saveTrackMetadata: (...args: Array<any>) => any
  sendGenericError: (...args: Array<any>) => any
  getWriters: (...args: Array<any>) => any
  saveAction: boolean
  inlineValidations: boolean
  updateWriterIds: (arg0: Array<number>) => void
  constanceSettings: Record<string, any>
  /** Hide the footer's "Copy track info" when the surrounding chrome
   *  already offers it (e.g. the track edit modal header). */
  hideCopyMetadata?: boolean
  loadTrackRightsData: (payload: {
    trackId: TrackId
    writerSplits: WriterSplit[]
  }) => void
}

class TrackWriters extends Component<
  Props,
  {
    editing: string[]
    editedSplit: number | null | undefined
    editServerError: string | null | undefined
    addServerError: string | null | undefined
    savingWriters: boolean
    listWritersUnsavedChanges: Record<string, any>[]
    listWriters: Record<string, any>[]
    errorInListWriters: boolean
    defaultWriters: Record<string, any>[]
  }
> {
  state: {
    editing: string[]
    editedSplit: number | null | undefined
    editServerError: string | null | undefined
    addServerError: string | null | undefined
    savingWriters: boolean
    listWritersUnsavedChanges: Record<string, any>[]
    listWriters: Record<string, any>[]
    errorInListWriters: boolean
    defaultWriters: Record<string, any>[]
  } = {
    editing: [],
    editedSplit: null,
    editServerError: null,
    addServerError: null,
    savingWriters: false,
    listWritersUnsavedChanges: [],
    listWriters: [],
    errorInListWriters: false,
    defaultWriters: [],
  }
  _onEditSwitch = id => {
    const { listWriters } = this.state
    const newValue = this.state.editedSplit === id ? null : id
    const newListWriters = [...listWriters]
    const index = listWriters.findIndex(
      item => item.props.split.id === this.state.editedSplit,
    )

    if (index >= 0) {
      newListWriters.splice(index, 1)
    }

    this.setState({
      listWriters: newListWriters,
      editedSplit: newValue,
      editServerError: null,
    })
  }
  _sendUnknownError = error => {
    const errorString = parseServerError(error.body)
    if (errorString !== 'Sum of splits cannot be higher than 100%')
      this.props.sendGenericError(error)
  }
  _onUpdate = split => {
    return this.props
      .onUpdate(split)
      .then(() =>
        this.setState({
          editedSplit: null,
          editServerError: null,
          listWritersUnsavedChanges: [],
        }),
      )
      .catch(error => {
        this._sendUnknownError(error)

        this.setState({
          editServerError: parseServerError(error.body),
        })
        throw error
      })
  }
  _onAdd = split => {
    return this.props
      .onAdd(split)
      .then(() =>
        this.setState({
          editedSplit: null,
          addServerError: null,
          listWritersUnsavedChanges: [],
        }),
      )
      .catch(error => {
        this._sendUnknownError(error)

        const serverError = parseServerError(error.body)
        const formattedError = this.props.bulkEditingMode
          ? `${serverError}. Please check the existing splits of the tracks you’re trying to apply this split to.`
          : serverError
        this.setState({
          addServerError: formattedError,
        })
        throw error
      })
  }
  _preloadWriters = async () => {
    try {
      const { results } = await api.getWriters('', 20)
      this.setState({
        defaultWriters: results,
      })
    } catch {}
  }
  _checkWriters = () => {
    const { editedTrack, splitWriterIds } = this.props
    const trackWriterIds = editedTrack.track_writers?.map(w => w.writer)

    // Checking writers are all in state (splits) if network is disconnected, if not, writers are updated and dispatched to state
    if (trackWriterIds && trackWriterIds.length !== splitWriterIds?.length) {
      return trackWriterIds
    }
  }

  componentDidMount() {
    // automatically add new empty split for editing when user edits multiple tracks
    if (this.props.editedTrack.id === null) this._handleAddEmptyWriter()

    if (this.props.constanceSettings?.SPLITS_V2) {
      // Load track rights data into rightsManagement reducer when SPLITS_V2 is enabled
      const trackId = this.props.editedTrack.id
      if (trackId != null) {
        this.props.loadTrackRightsData({
          trackId,
          writerSplits: this.props.editedTrack.track_writers || [],
        })
      }
    } else {
      // only preload writers if splits v2 is not enabled
      this._preloadWriters()
    }
  }

  componentDidUpdate() {
    if (
      this.props.splits.length === 0 &&
      this.state.editing.length === 0 &&
      this.props.editedTrack.track_writers?.length === 0
    ) {
      this._handleAddEmptyWriter()
    }
  }

  _trackContactsClick = () => {
    mixpanel.sendPureEvent('Open contacts via link in Writers split tab')
  }
  _trackBusinessSettingsClick = () => {
    mixpanel.sendPureEvent(
      'Open business settings via link in Writers split tab',
    )
  }
  _handleAddEmptyWriter = () => {
    this.setState({
      editing: [cuid(), ...this.state.editing],
    }) // single item can be active for editing
    // if (this.state.editing.length === 0)
    //   this.setState({ editing: [cuid()], listWriters: [], editedSplit: null })
  }
  _handleCancelAddingNew = removing => {
    this.setState({
      editing: this.state.editing.filter(id => id !== removing),
    }) // this.setState({ editing: [] })
  }

  _updateMetadataField(
    key: string,
    value: string | number | File | null | typeof undefined,
  ) {
    const editedTrack = this.props.editedTrack
    const metadata = editedTrack.metadata
    this.props.updateEditedTrack({
      ...editedTrack,
      metadata: {
        ...metadata,
        [key]: { ...metadata[key], mixed: false, value },
      },
    })
  }

  _handleSubmit = () => {
    this.props.saveTrackMetadata()
    this.props.sendMixpanelEvent('Metadata edit')
  }
  getWriters = (writer, closeId) => {
    const { listWriters } = this.state
    let newListWriters = [...listWriters]

    if (closeId) {
      newListWriters = listWriters.filter(
        item => item.props.split.id !== closeId,
      )
      this.setState({
        listWriters: newListWriters,
      })
    } else {
      const isSave = listWriters.find(item => {
        return item.props.split.id === writer.props.split.id
      })

      if (!isSave) {
        this.setState({
          listWriters: [...listWriters, writer],
        })
      }
    }
  }
  _saveWriterHandler = async (writer, isUpdate) => {
    try {
      await writer.onSubmit()

      if (isUpdate) {
        this.setState(prevState => {
          return {
            editing: prevState.editing.filter(
              id => id !== writer.props.split.id,
            ),
            editedSplit: null,
          }
        })
      } else {
        this.setState(prevState => {
          return {
            listWriters: prevState.listWriters.filter(
              w => w.props.split.id !== writer.props.split.id,
            ),
            editing: prevState.editing.filter(
              id => id !== writer.props.split.id,
            ),
          }
        })
      }
    } catch (error) {
      // don't throw errors, we need all promises to resolve
    }
  }
  _handleSaveWriters = async () => {
    const { listWriters } = this.state
    this.setState({
      savingWriters: listWriters.length > 0,
    })
    const updatedWriters = listWriters
      .filter(writer => writer.props.split.id === this.state.editedSplit)
      .sort((a, b) => {
        const splitA = a.getInputs ? a.getInputs().split || 0 : 0
        const splitB = b.getInputs ? b.getInputs().split || 0 : 0
        return splitA - splitB // Sort in ascending order (lowest percentage first)
      })
    const insertedWriters = listWriters.filter(
      writer => writer.props.split.id !== this.state.editedSplit,
    )

    // Process updated writers first to avoid split validation errors
    for (const writer of updatedWriters) {
      await this._saveWriterHandler(writer, true)
    }

    // Process inserted writers after updated writers to avoid split validation errors
    for (const writer of insertedWriters) {
      await this._saveWriterHandler(writer, false)
    }

    this.setState({
      savingWriters: false,
    })
  }

  checkSumSplits() {
    const splitsOfCheck = {}
    ;[...this.props.splits, ...this.state.listWriters].forEach(item => {
      if (item.props && item.getInputs) {
        splitsOfCheck[item.props.split.id] = item.getInputs().split || 0
      } else {
        splitsOfCheck[item.id] = item.split || 0
      }
    })
    const commonSplit = parseFloat(
      Object.keys(splitsOfCheck)
        .reduce((accumulator, key) => {
          // Calculation to fix floating point issue
          return accumulator + Number(splitsOfCheck[key])
        }, 0)
        .toFixed(2),
    )

    if (commonSplit > 100) {
      this.setState({
        addServerError: 'Sum of splits percentages exceeds 100%',
      })
    } else if (this.state.addServerError) {
      this.setState({
        addServerError: null,
      })
    }
  }

  handleWritersUnsavedChanges = ({ id, hasChanges, isValid }) => {
    const list = this.state.listWritersUnsavedChanges
    const isPushed = list.find(itemId => itemId === id)
    const result = isPushed
      ? list
          .map(itemId => {
            if (itemId === id && !hasChanges) {
              return null
            }

            return itemId
          })
          .filter(itemId => itemId)
      : [...list, id]
    this.setState({
      listWritersUnsavedChanges: result,
      errorInListWriters: !isValid,
    })
    this.checkSumSplits()
  }

  render() {
    const { splits, onDelete, editedTrack, splitWriterIds, constanceSettings } =
      this.props
    let editingKeys = this.state.editing
    const unsavedChanges =
      this.props.hasUnsavedChanges ||
      this.state.listWritersUnsavedChanges.length
    const savingProgress = this.state.savingWriters || this.props.saving
    const errorText = this.state.addServerError || this.state.editServerError
    const isError: boolean = Boolean(errorText) || this.state.errorInListWriters

    return (
      <div>
        <div className="track-writers">
          <Heading size="700">
            Manage Writers
            <button onClick={this._handleAddEmptyWriter}>
              <MdAddCircleOutline />
            </button>
          </Heading>

          {editingKeys.map(editingKey => (
            <EditWriter
              key={editingKey}
              editingKey={editingKey}
              track={editedTrack.id}
              exclusionList={splitWriterIds}
              type="add"
              split={{
                id: editingKey,
              }}
              serverError={this.state.addServerError}
              onSubmit={this._onAdd}
              onCancel={this._handleCancelAddingNew}
              getWriters={this.getWriters}
              saveAction={false}
              inlineValidations
              isFirstWriter={!splits.length && editingKeys.length < 2}
              onUnsavedChanges={this.handleWritersUnsavedChanges}
              listOfWritersToCreate={this.state.listWriters}
              defaultWriters={this.state.defaultWriters}
            />
          ))}

          {splits.length > 0 && (
            <div>
              {/* {header} */}
              {splits.map((w, i) => (
                <TrackWriter
                  trackId={editedTrack.id}
                  key={`writer-${i}`}
                  split={w}
                  editExclusionList={splits
                    .filter(s => s.writer.id !== w.writer.id)
                    .map(s => s.writer.id)}
                  editMode={this.state.editedSplit === w.id}
                  editingKey={this.state.editedSplit}
                  serverError={this.state.editServerError}
                  onEdit={this._onEditSwitch}
                  disableEditing={this.props.disableEditing}
                  onUpdate={this._onUpdate}
                  onDelete={onDelete}
                  getWriters={this.getWriters}
                  saveAction={false}
                  inlineValidations
                  onUnsavedChanges={this.handleWritersUnsavedChanges}
                  listOfWritersToCreate={this.state.listWriters}
                  defaultWriters={this.state.defaultWriters}
                  trackWriterIds={this._checkWriters()}
                  updateWriterIds={this.props.updateWriterIds}
                />
              ))}
            </div>
          )}
          {errorText && (
            <Box mb="4">
              <ErrorText>{errorText}</ErrorText>
            </Box>
          )}
          <br />
          <div className="row flc">
            <div className="col-xs-3">
              <div className="Form_Label">
                PRO Number
                <HelpTooltip id="tooltip_metadata_modal_pro_number" />
              </div>
              <TextField
                value={editedTrack.metadata['ProNumber']?.value || ''}
                onChange={evt => {
                  this._updateMetadataField('ProNumber', evt.target.value)
                }}
              />
            </div>
          </div>
        </div>

        <DialogFooter
          primary={
            <ButtonContainer>
              {!this.props.hideCopyMetadata &&
                this.props.editedTrack.trackIds && (
                  <CopyMetadataButton
                    trackIds={this.props.editedTrack.trackIds}
                  />
                )}
              {!this.props.hideCopyMetadata && this.props.editedTrack.id && (
                <CopyMetadataButton trackIds={[this.props.editedTrack.id]} />
              )}
              <Button
                onClick={() => {
                  this._handleSaveWriters()

                  if (this.props.hasUnsavedChanges) {
                    this._handleSubmit()
                  }
                }}
                appearance="primary"
                caption="Save"
                progressIndicator={savingProgress}
                progressCaption="Saving"
                disabled={!unsavedChanges || isError}
              />
            </ButtonContainer>
          }
          corner={
            <>
              <FormNote className="form-note--spaced" displayIcon={false}>
                Writers splits are currently set to{' '}
                <b>
                  {this.props.attach_writers_to_metadata ? null : (
                    <span>not</span>
                  )}
                </b>{' '}
                write to metadata on this business.{' '}
                <InlineLink
                  to="/settings/business"
                  onClick={this._trackBusinessSettingsClick}
                >
                  Change that here
                </InlineLink>
                .
              </FormNote>
              <FormNote displayIcon={false}>
                To manage your writers, visit{' '}
                <InlineLink
                  to="/contacts/list"
                  onClick={this._trackContactsClick}
                >
                  Contacts
                </InlineLink>
              </FormNote>
            </>
          }
        />
      </div>
    )
  }
}

export default connect(mapStateToProps, {
  sendGenericError,
  loadTrackRightsData,
})(TrackWriters) as any
